import { useEffect, useState } from 'react';
import {
  ref,
  set,
  remove,
  onValue,
  onDisconnect,
  serverTimestamp,
} from 'firebase/database';
import { db } from '../firebase';
import { dlog } from './debug';

// Presence is considered "live" if its lastSeen timestamp is within
// this window. Heartbeat keeps it fresh; onDisconnect handles tab close.
const PRESENCE_TTL_MS = 60 * 1000;
const HEARTBEAT_MS = 20 * 1000;

/**
 * Writes the current user's presence to matches/{matchId}/presence/{uid}.
 * Sets up onDisconnect cleanup so an abrupt tab close releases the slot
 * within a few seconds. Heartbeats every 20s to keep lastSeen fresh.
 *
 * Call from a viewer/read-only surface only — scorers (creators
 * editing their own match) shouldn't count toward "N watching."
 *
 * Returns nothing; the effect handles its own lifecycle.
 */
export function useWriteViewerPresence({ matchId, user, enabled = true }) {
  useEffect(() => {
    if (!enabled || !matchId || !user) return;
    const presenceRef = ref(db, `matches/${matchId}/presence/${user.uid}`);

    // Initial write + set up disconnect cleanup before any async wait.
    onDisconnect(presenceRef).remove().catch((err) => {
      dlog('presence onDisconnect setup failed:', err);
    });
    set(presenceRef, { lastSeen: serverTimestamp() }).catch((err) => {
      dlog('presence initial write failed:', err);
    });

    const heartbeat = setInterval(() => {
      set(presenceRef, { lastSeen: serverTimestamp() }).catch((err) => {
        dlog('presence heartbeat failed:', err);
      });
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(heartbeat);
      // Best-effort clean removal on unmount. onDisconnect covers tab close.
      remove(presenceRef).catch(() => {});
    };
  }, [matchId, user, enabled]);
}

/**
 * Subscribes to matches/{matchId}/presence and returns a live count of
 * non-stale viewers, optionally excluding a specific uid (the scorer's
 * own presence shouldn't count toward their "N watching" indicator —
 * though scorers don't write presence in the first place, so this is
 * a defensive filter).
 *
 * @returns number — current viewer count, or 0 when disabled
 */
export function useViewerCount({ matchId, excludeUid = null, enabled = true }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled || !matchId) {
      setCount(0);
      return;
    }
    const presenceRef = ref(db, `matches/${matchId}/presence`);
    const unsubscribe = onValue(presenceRef, (snapshot) => {
      if (!snapshot.exists()) {
        setCount(0);
        return;
      }
      const now = Date.now();
      let n = 0;
      snapshot.forEach((child) => {
        if (excludeUid && child.key === excludeUid) return;
        const lastSeen = child.val()?.lastSeen;
        if (typeof lastSeen === 'number' && now - lastSeen < PRESENCE_TTL_MS) {
          n += 1;
        }
      });
      setCount(n);
    });
    return unsubscribe;
  }, [matchId, excludeUid, enabled]);

  return count;
}
